import type { KeyboardEvent, MouseEvent } from 'react';
import { BookOpen } from 'lucide-react';

import { AppRoutes } from '@/constants/routes';
import { cn } from '@/utils/cn';

/**
 * "View ledger" from any GL account field (422175). Chart of Accounts has F9 + a Ledger button that
 * navigate in place; on a transaction screen that would throw away the half-keyed document, so here
 * the ledger opens in a new tab (the tab restores its session from the refresh cookie).
 */
export const openAccountLedger = (accountId: string) =>
  window.open(`${AppRoutes.REPORTS_ACCOUNT_LEDGER}?accountId=${accountId}`, '_blank', 'noopener');

/**
 * F9 while focus is anywhere inside the element this is attached to (as onKeyDownCapture, so it runs
 * before the focused control can swallow the key — see d27c114). Skips a key a page-level F9 already
 * handled, e.g. Chart of Accounts' own shortcut for the loaded account.
 */
export const ledgerShortcut = (accountId: string | null | undefined) => (e: KeyboardEvent) => {
  if (e.key !== 'F9' || !accountId || e.defaultPrevented) return;
  e.preventDefault();
  openAccountLedger(accountId);
};

interface AccountLedgerButtonProps {
  accountId: string | null | undefined;
  /** Show "Ledger" text next to the icon (for fields with no room inside the box). */
  withText?: boolean;
  className?: string;
}

export const AccountLedgerButton = ({ accountId, withText, className }: AccountLedgerButtonProps) => {
  if (!accountId) return null;
  return (
    <button
      type="button"
      title="View this account's ledger listing in a new tab (F9)"
      // Inside clickable pickers: the click must not also open the account search.
      onClick={(e: MouseEvent) => { e.stopPropagation(); openAccountLedger(accountId); }}
      className={cn(
        'flex-shrink-0 inline-flex items-center gap-1 text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors',
        withText && 'text-[11px] font-medium',
        className,
      )}
    >
      <BookOpen size={12} />
      {withText && <span>Ledger <span className="text-slate-400 font-normal">F9</span></span>}
    </button>
  );
};
